"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Clock, MapPin, ShieldCheck, Info as HelpCircle, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "What is your typical emergency response time in Jonesboro?",
  answer: "Our technicians arrive on-site within 30 to 60 minutes for critical plumbing emergencies across Jonesboro and immediate Clayton County areas. We dispatch the nearest available crew as soon as you confirm your call."
}, {
  id: "item-2",
  question: "What specific areas and boundaries do you cover?",
  answer: "We provide complete coverage throughout Jonesboro (GA 30236, 30238) and neighboring communities including Morrow, Forest Park, Riverdale, Lovejoy, Stockbridge, and Fayetteville. If you are anywhere in Clayton County or South Metro Atlanta, our team can reach you."
}, {
  id: "item-3",
  question: "Are your emergency plumbing services truly available 24/7?",
  answer: "Yes. We operate 24 hours a day, 7 days a week, 365 days a year—including late nights, weekends, and all major holidays. Emergency plumbing failures never wait for business hours, and neither do we."
}, {
  id: "item-4",
  question: "What types of plumbing emergencies do you fix?",
  answer: "We tackle burst or leaking water pipes, failed water heaters, overflowing toilets, severe main sewer line backups, clogged commercial or residential drains, slab leaks, and gas line issues requiring immediate shut-off and repair."
}, {
  id: "item-5",
  question: "How do I request emergency service right now?",
  answer: "For immediate help, call our emergency dispatcher directly. We collect your address, evaluate the urgency, guide you through emergency shut-off steps over the phone if water is actively flooding, and dispatch a licensed technician right away."
}, {
  id: "item-6",
  question: "Do you provide upfront pricing before starting emergency work?",
  answer: "Yes. Once our plumber inspects the problem on-site, we explain the issue clearly and give you a clear, upfront estimate before any work begins. There are no surprise hidden fees."
}];
const highlights = [{
  icon: Clock,
  title: "30-60 Min Response",
  description: "Rapid local dispatch across Jonesboro"
}, {
  icon: MapPin,
  title: "Clayton County Wide",
  description: "Serving all nearby communities"
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  description: "Certified master plumbers on call"
}];
export default function FaqAccordion() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="faqaccordion" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Context & Direct Emergency Call Box */}
          <motion.div className="lg:col-span-5 flex flex-col gap-6" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants}>
            <motion.div variants={itemVariants} className="flex flex-col gap-3">
              <Badge variant="secondary" className="w-fit flex items-center gap-1.5 px-3 py-1">
                <HelpCircle className="size-3.5 text-primary" />
                <span>Common Questions</span>
              </Badge>
              <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground leading-tight">
                Frequently Asked Questions About Our Emergency Services
              </h2>
              <p className="font-default text-base text-muted-foreground leading-relaxed">
                Get clear answers about service boundaries, average arrival times, and how our 24/7 dispatch works in Jonesboro, GA.
              </p>
            </motion.div>

            {/* Service Guarantee Highlights */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3 pt-2">
              {highlights.map((item, idx) => {
              const IconComponent = item.icon;
              return <div key={idx} data-index={idx} className="flex items-start gap-3 p-3.5 rounded-lg bg-card border border-border">
                    <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                      <IconComponent className="size-4" />
                    </div>
                    <div>
                      <h3 className="font-default text-sm font-semibold text-card-foreground">
                        {item.title}
                      </h3>
                      <p className="font-default text-xs text-muted-foreground mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </div>;
            })}
            </motion.div>

            {/* Callout Support Box */}
            <motion.div variants={itemVariants} className="p-6 rounded-xl bg-primary text-primary-foreground flex flex-col gap-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-full bg-accent text-accent-foreground shrink-0">
                  <PhoneCall className="size-5" />
                </div>
                <div>
                  <div className="text-xs uppercase font-semibold tracking-wide text-primary-foreground/80">
                    Need Immediate Dispatch?
                  </div>
                  <div className="text-lg font-bold">
                    Call 24/7 Emergency Line
                  </div>
                </div>
              </div>
              <p className="text-xs text-primary-foreground/90 leading-relaxed">
                Do not wait for water damage to spread. Speak with an on-duty Jonesboro dispatcher now for rapid emergency assistance.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button asChild variant="default" size="default" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-xs justify-center">
                  <a href="tel:4708479076" className="flex items-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>(470) 847-9076</span>
                  </a>
                </Button>
                <Button asChild variant="outline" size="default" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground justify-center">
                  <Link to="/contact/" className="flex items-center gap-1.5">
                    <span>Send Message</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column: Accordion */}
          <motion.div className="lg:col-span-7" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={itemVariants}>
            <div className="rounded-xl border border-border bg-card p-4 sm:p-6 shadow-xs">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                {faqItems.map((faq, faqIndex) => <AccordionItem key={faq.id} value={faq.id} data-index={faqIndex} className="border-b border-border last:border-b-0 py-1">
                    <AccordionTrigger className="font-default text-base font-semibold text-card-foreground hover:text-primary transition-colors py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-default text-sm text-muted-foreground leading-relaxed pt-1 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>

              <div className="mt-6 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-xs text-muted-foreground text-center sm:text-left">
                  Have a specific question about repairs or replacement?
                </div>
                <Button asChild variant="secondary" size="sm" className="shrink-0">
                  <Link to="/services/" className="flex items-center gap-1.5">
                    <span>View All Services</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}